import api from './api';

// ── FTO report types (mirrors backend models.py) ─────────────────────────────

export interface FeatureMapping {
  feature_id: string;
  mark: number;        // 0–5
  evidence: string;
  target_location: string;
}

export interface FeatureMappingResult {
  artifact_id: string;
  claim_index: number;
  target_kind: string;
  target_id: string;
  mappings: FeatureMapping[];
}

export interface FeatureVerdict {
  feature_id: string;
  is_present: boolean;
  confidence: number;  // 0–100
  rationale: string;
}

export interface ClaimVerdictResult {
  claim_index: number;
  target_id: string;
  verdict: string;     // "infringed" | "not infringed"
  is_met: boolean;
  confidence: number;
  rationale: string;
  feature_verdicts: FeatureVerdict[];
}

export interface FtoClaimReport {
  patent_id: string;
  claim_index: number;
  artifact_id: string;
  mapping: FeatureMappingResult | null;
  verdict: ClaimVerdictResult | null;
}

export interface FtoPatentReport {
  patent_id: string;
  claims_analysed: FtoClaimReport[];
  worst_verdict: string;
}

export interface FtoReport {
  id: string;
  solution_id: string;
  solution_html_hash: string;
  status: 'running' | 'completed' | 'failed';
  error?: string | null;
  countries: string[];
  patents: FtoPatentReport[];
  country_matrix: Record<string, Record<string, string>>;
  created_at: string;
  model_ids_used: string[];
}

export interface FtoCostEstimate {
  patent_count: number;
  patents_with_claims: number;
  estimated_llm_calls: number;
  estimated_cost_cents: number;
  currency: string;
  model_id: string;
  model_name: string | null;
}

// ── Claim analysis artifact types ────────────────────────────────────────────

export type FeatureClass =
  | 'clear_structural'
  | 'clear_functional'
  | 'vague_or_relative'
  | 'means_plus_function'
  | 'markush_or_alternative';

export type FeatureRelation = 'conjunctive' | 'alternative' | 'optional';

export interface RefinedFeature {
  feature_id: string;
  parent_id: string | null;
  relation: FeatureRelation;
  text: string;
  construed_meaning: string;
  construction_evidence: string;
  feature_class: FeatureClass;
  is_optional: boolean;
  alternative_group_id: string | null;
}

export interface ClaimAnalysisArtifact {
  id: string;
  source_kind: string;
  source_id: string;
  claim_index: number;
  claim_text: string;
  claim_text_hash: string;
  model_id: string;
  prompt_version: string;
  created_at: string;
  features: RefinedFeature[];
  claim_structure: {
    preamble: string;
    two_part_form: boolean;
    has_alternatives: boolean;
    has_optional_features: boolean;
  };
}

const FTO_BASE = '/patent-analysis/fto';
const ANALYSIS_BASE = '/patent-analysis';

export const ftoAnalysisService = {
  getReport: async (
    subProjectId: string,
    solutionId: string,
  ): Promise<FtoReport> => {
    const { data } = await api.get<FtoReport>(
      `${FTO_BASE}/reports/${solutionId}?sub_project_id=${subProjectId}`,
    );
    return data;
  },

  getEstimate: async (
    subProjectId: string,
    solutionId: string,
  ): Promise<FtoCostEstimate> => {
    const { data } = await api.get<FtoCostEstimate>(
      `${FTO_BASE}/estimate/${solutionId}?sub_project_id=${subProjectId}`,
    );
    return data;
  },

  launch: async (
    subProjectId: string,
    solutionId: string,
  ): Promise<{ status: string; message: string }> => {
    const { data } = await api.post(`${FTO_BASE}/launch`, {
      sub_project_id: subProjectId,
      solution_id: solutionId,
    });
    return data;
  },

  getClaimArtifact: async (
    subProjectId: string,
    artifactId: string,
  ): Promise<ClaimAnalysisArtifact> => {
    const { data } = await api.get<ClaimAnalysisArtifact>(
      `${ANALYSIS_BASE}/claim-artifacts/${artifactId}?sub_project_id=${subProjectId}`,
    );
    return data;
  },
};
